import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, X, Settings } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { entities } from '../../api/client';
import { fetchAllClients } from '../../services/clientApi';
import { useClient } from '../../context/ClientContext';

import useTrainingPlanStore from '../../stores/trainingPlanStore';
import { hydratePlan, serializePlan, emptyToNull } from '../../utils/trainingPlanSchema';

import PlanWeekGrid from '../../components/training/PlanWeekGrid';
import WorkoutEditDrawer from '../../components/training/WorkoutEditDrawer';
import ExerciseSettingsDrawer from '../../components/training/ExerciseSettingsDrawer';
import PlanMetaBar from '../../components/training/PlanMetaBar';
import AdvancedPlanSettings from '../../components/training/AdvancedPlanSettings';
import ConfirmDialog from '../../components/training/ConfirmDialog';

export default function PlanBuilder() {
 const { planId } = useParams();
 const navigate = useNavigate();
 const location = useLocation();
 const { planFormData: initialFormData } = location.state || {};
 const { themeClasses } = useTheme();
 const { selectedClient } = useClient();

 const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
 const [isSaving, setIsSaving] = useState(false);
 const [isLoading, setIsLoading] = useState(!!planId && !initialFormData);
 
 const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(!initialFormData?.plan_name && !planId);
 const [settingsAttempted, setSettingsAttempted] = useState(false);
 const [clients, setClients] = useState([]);
 const [clientError, setClientError] = useState(false);
 const [isLoadingClients, setIsLoadingClients] = useState(false);
 const [clientSearch, setClientSearch] = useState('');
 
 // Zustand store hooks
 const initPlan = useTrainingPlanStore(state => state.initPlan);
 const assignment = useTrainingPlanStore(state => state.assignment);
 const meta = useTrainingPlanStore(state => state.meta);
 const weekOrder = useTrainingPlanStore(state => state.weekOrder);
 const workoutOrder = useTrainingPlanStore(state => state.workoutOrder);
 const updateAssignment = useTrainingPlanStore(state => state.updateAssignment);
 const updateMeta = useTrainingPlanStore(state => state.updateMeta);

 const [localName, setLocalName] = useState('');
 const [localNotes, setLocalNotes] = useState('');
 
 useEffect(() => {
 setLocalName(meta.name || '');
 }, [meta.name]);
 
 useEffect(() => {
 setLocalNotes(assignment.notes || '');
 }, [assignment.notes]);

 const handleBlur = (field, localValue) => {
 if (field === 'name' && localValue !== meta.name) {
 updateMeta({ name: localValue });
 }
 if (field === 'notes' && localValue !== assignment.notes) {
 updateAssignment({ notes: localValue });
 }
 };

 useEffect(() => {
 const handleStorageChange = () => setLanguage(localStorage.getItem('language') || 'english');
 window.addEventListener('storage', handleStorageChange);
 window.addEventListener('languageChange', handleStorageChange);
 return () => {
 window.removeEventListener('storage', handleStorageChange);
 window.removeEventListener('languageChange', handleStorageChange);
 };
 }, []);

 const fetchClientsList = () => {
 setIsLoadingClients(true);
 setClientError(false);
 fetchAllClients().then(data => {
 const list = Array.isArray(data) ? data : (data?.clients ? data.clients : []);
 setClients(list);
 }).catch(err => {
 console.error(err);
 setClientError(true);
 }).finally(() => {
 setIsLoadingClients(false);
 });
 };

 useEffect(() => {
 fetchClientsList();
 }, []);

 const [saveError, setSaveError] = useState(null);
 const [activeConfirmOpen, setActiveConfirmOpen] = useState(false);
 const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
 const [availableDraft, setAvailableDraft] = useState(null);
 const [isOffline, setIsOffline] = useState(!navigator.onLine);

 useEffect(() => {
 const handleOnline = () => setIsOffline(false);
 const handleOffline = () => setIsOffline(true);
 window.addEventListener('online', handleOnline);
 window.addEventListener('offline', handleOffline);
 return () => {
 window.removeEventListener('online', handleOnline);
 window.removeEventListener('offline', handleOffline);
 };
 }, []);

 // Load plan and manage drafts
 useEffect(() => {
 const draftKey = `training-plan-draft:${planId || 'new'}`;
 const loadPlan = async () => {
 try {
 let storeData;
 
 const draftStr = localStorage.getItem(draftKey);
 let parsedDraft = null;
 if (draftStr) {
 try {
 parsedDraft = JSON.parse(draftStr);
 } catch (e) {
 console.error("Failed to parse draft", e);
 }
 }
 
 if (planId && !initialFormData) {
 const planData = await entities.TrainingPlans.getById(planId);
 storeData = hydratePlan(planData);
        } else if (initialFormData) {
          storeData = hydratePlan(initialFormData);
        } else {
          storeData = hydratePlan({ user_code: selectedClient?.user_code || selectedClient?.id });
        }
 
 initPlan(storeData);
 if (parsedDraft) {
 setAvailableDraft(parsedDraft);
 }
 } catch (err) {
 console.error('Error fetching plan:', err);
 setSaveError(language === 'hebrew' ? 'שגיאה בטעינת התוכנית' : 'Failed to load training plan');
 } finally {
 setIsLoading(false);
 }
 };
 loadPlan();
 }, [planId, initialFormData, initPlan, language]);

 const handleRestoreDraft = () => {
 if (availableDraft) {
 initPlan(availableDraft);
 setAvailableDraft(null);
 }
 };

 const handleDiscardDraft = () => {
 const draftKey = `training-plan-draft:${planId || 'new'}`;
 localStorage.removeItem(draftKey);
 setAvailableDraft(null);
 };

 // Persist draft periodically
 useEffect(() => {
 if (isLoading) return;
 const saveDraft = () => {
 const state = useTrainingPlanStore.getState();
 if (state.weekOrder.length === 0 && !state.meta.name) return;
 const draftKey = `training-plan-draft:${planId || 'new'}`;
 localStorage.setItem(draftKey, JSON.stringify(state));
 };
 
 document.addEventListener('visibilitychange', saveDraft);
 const interval = setInterval(saveDraft, 10000);
 
 return () => {
 document.removeEventListener('visibilitychange', saveDraft);
 clearInterval(interval);
 saveDraft();
 };
 }, [isLoading, planId]);

 const missingClient = !assignment.user_code;
 const missingName = !(meta.name || '').trim();
 const missingStart = !assignment.active_from;
 const settingsIncomplete = missingClient || missingName || missingStart;

 const closeSettingsDrawer = () => {
 if (settingsIncomplete) {
 setSettingsAttempted(true);
 return;
 }
 setIsSettingsDrawerOpen(false);
 setSettingsAttempted(false);
 };

  const handleSavePlan = async () => {
    setSaveError(null);
    if (settingsIncomplete) {
      setIsSettingsDrawerOpen(true);
      setSettingsAttempted(true);
      return;
    }
    
    try {
 setIsSaving(true);
 
 const plan_structure = serializePlan(useTrainingPlanStore.getState());
 
 // Calculate active_until based on start date + duration weeks (optional, depends on logic, keeping current for now)
 const payload = {
 user_code: emptyToNull(assignment.user_code),
 plan_name: emptyToNull(meta.name),
 description: null,
 goal: emptyToNull(assignment.goal),
 difficulty_level: emptyToNull(assignment.difficulty_level),
 duration_weeks: meta.duration_weeks ?? null,
 weekly_frequency: 7, // Adjusting since it's a grid now
 status: emptyToNull(assignment.status),
 active_from: emptyToNull(assignment.active_from),
 active_until: emptyToNull(assignment.active_until),
 notes: emptyToNull(assignment.notes),
 plan_structure
 };
 
 if (planId) {
 await entities.TrainingPlans.update(planId, payload);
 } else {
 await entities.TrainingPlans.create(payload);
 }
 
 localStorage.removeItem(`training-plan-draft:${planId || 'new'}`);
 toast.success(language === 'hebrew' ? 'התוכנית נשמרה בהצלחה' : 'Training plan saved successfully');
 navigate('/training-management');
 } catch (err) {
 console.error('Failed to save plan:', err);
 setSaveError(language === 'hebrew' ? 'שגיאה בשמירת התוכנית. אנא נסה שוב.' : 'Failed to save training plan. Please try again.');
 } finally {
 setIsSaving(false);
 }
 };

 if (isLoading) {
 return (
 <div className={`h-[calc(100dvh-56px)] flex flex-col bg-canvas dark:bg-canvas-dark overflow-hidden`}>
      <header className="shrink-0 h-16 border-b border-line dark:border-line-dark bg-surface dark:bg-canvas-dark sticky top-0 z-20 flex items-center px-4 animate-pulse">
        <div className="w-10 h-10 rounded-full bg-line dark:bg-line-dark mr-4"></div>
        <div className="h-6 w-32 bg-line dark:bg-line-dark rounded"></div>
        <div className="flex-1"></div>
        <div className="w-24 h-10 rounded-md bg-line dark:bg-line-dark mr-3"></div>
        <div className="w-24 h-10 rounded-md bg-line dark:bg-line-dark"></div>
      </header>
      <main className="flex-1 p-4 md:p-8 relative">
        <div className="max-w-[1400px] mx-auto space-y-4 animate-pulse">
          <div className="flex gap-2">
            {Array.from({ length: 7 }).map((_, i) => <div key={i} className="flex-1 h-6 bg-line dark:bg-line-dark rounded"></div>)}
          </div>
          <div className="flex gap-2">
            <div className="w-16 h-40 bg-line dark:bg-line-dark rounded"></div>
            {Array.from({ length: 7 }).map((_, i) => <div key={i} className="flex-1 h-40 bg-line dark:bg-line-dark rounded-md"></div>)}
          </div>
          <div className="flex gap-2">
            <div className="w-16 h-40 bg-line dark:bg-line-dark rounded"></div>
            {Array.from({ length: 7 }).map((_, i) => <div key={i} className="flex-1 h-40 bg-line dark:bg-line-dark rounded-md"></div>)}
          </div>
        </div>
      </main>
 </div>
 );
 }

 const clientName = clients.find(c => c.user_code === assignment.user_code)?.full_name 
 || clients.find(c => c.user_code === assignment.user_code)?.name 
 || assignment.user_code 
 || (language === 'hebrew' ? 'לא נבחר לקוח' : 'No Client Selected');

 return (
 <div className={`h-[calc(100dvh-56px)] flex flex-col bg-canvas dark:bg-canvas-dark ${themeClasses.textPrimary} overflow-hidden`}>
 
 {/* Header */}
      <header className={`shrink-0 h-16 flex items-center px-4 md:px-6 border-b border-line dark:border-line-dark bg-surface dark:bg-canvas-dark sticky top-0 z-20`}>
        <button 
          onClick={() => navigate('/training-management')}
          className="me-2 sm:me-4 p-2 min-h-11 min-w-11 rounded-full hover:bg-canvas dark:hover:bg-surface-dark transition-colors flex items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-forest"
          aria-label={language === 'hebrew' ? 'חזור' : 'Go back'}
        >
          <ArrowLeft size={20} className={language === 'hebrew' ? 'rotate-180' : ''} />
        </button>
        
        <div className="flex min-w-0 flex-col flex-1 md:flex-none">
          <h1 className="text-base sm:text-xl font-semibold tracking-tight truncate md:max-w-sm">
            {meta.name || (language === 'hebrew' ? 'תוכנית חדשה' : 'New Plan')}
          </h1>
          <div className="text-xs font-medium text-ink-muted flex min-w-0 items-center gap-1.5">
            <span className="truncate">{clientName}</span>
            <span>&bull;</span>
            <span>{meta.duration_weeks || weekOrder.length} {language === 'hebrew' ? 'שבועות' : 'Weeks'}</span>
          </div>
        </div>

        {/* Step Bar */}
        <div className="hidden md:flex flex-1 items-center justify-center mx-8">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setIsSettingsDrawerOpen(true)}
              className={`flex items-center gap-2 text-sm font-semibold transition-colors ${settingsIncomplete ? 'text-ink-muted hover:text-ink' : 'text-forest'}`}
            >
              <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs ${settingsIncomplete ? 'bg-line text-ink-muted' : 'bg-forest text-white'}`}>1</div>
              {language === 'hebrew' ? 'פרטי תוכנית' : 'Plan info'}
            </button>
            <div className="w-8 h-px bg-line"></div>
            <div className={`flex items-center gap-2 text-sm font-semibold transition-colors ${!settingsIncomplete ? 'text-ink dark:text-ink-dark' : 'text-ink-muted dark:text-ink-muted-dark'}`}>
              <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs ${!settingsIncomplete ? 'bg-ink text-surface dark:bg-ink-dark dark:text-surface-dark' : 'bg-line dark:bg-line-dark text-ink-muted'}`}>2</div>
              {language === 'hebrew' ? 'תרגילים' : 'Exercises'}
            </div>
            <div className="w-8 h-px bg-line"></div>
            <button 
              onClick={() => setActiveConfirmOpen(true)}
              disabled={settingsIncomplete}
              className={`flex items-center gap-2 text-sm font-semibold transition-colors ${!settingsIncomplete ? 'text-ink-muted hover:text-ink' : 'text-line dark:text-line-dark'}`}
            >
              <div className="flex items-center justify-center w-6 h-6 rounded-full text-xs bg-line dark:bg-line-dark text-ink-muted">3</div>
              {language === 'hebrew' ? 'סקירה' : 'Review'}
            </button>
          </div>
        </div>
        
        <button
          onClick={() => setIsSettingsDrawerOpen(true)}
          aria-label={language === 'hebrew' ? 'הגדרות תוכנית' : 'Edit Info'}
          title={language === 'hebrew' ? 'הגדרות תוכנית' : 'Edit Info'}
          className="me-2 md:hidden flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-2 px-2 sm:px-3 text-sm font-medium hover:bg-canvas dark:hover:bg-surface-dark rounded-lg transition-colors"
        >
          <Settings size={18} /> <span className="hidden sm:inline">{language === 'hebrew' ? 'הגדרות תוכנית' : 'Edit Info'}</span>
        </button>
        
        <button 
          onClick={() => {
            if (settingsIncomplete) {
              setIsSettingsDrawerOpen(true);
              setSettingsAttempted(true);
            } else {
              setActiveConfirmOpen(true);
            }
          }}
          disabled={isSaving || isOffline}
          className="shrink-0 px-3 sm:px-4 py-2 bg-forest hover:bg-forest-hover text-white disabled:opacity-50 text-sm sm:text-base font-bold rounded-md transition-colors min-h-[44px]"
        >
          {isSaving ? (language === 'hebrew' ? 'שומר...' : 'Saving...') : settingsIncomplete ? (
            <><span className="sm:hidden">{language === 'hebrew' ? 'פרטים' : 'Plan info'}</span><span className="hidden sm:inline">{language === 'hebrew' ? 'השלם פרטים' : 'Finish plan info'}</span></>
          ) : (
            <><span className="sm:hidden">{language === 'hebrew' ? 'שמירה' : 'Save'}</span><span className="hidden sm:inline">{language === 'hebrew' ? 'סקור ושמור' : 'Review & Save'}</span></>
          )}
        </button>
      </header>

 {/* Canvas */}
 <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-8 custom-scrollbar relative bg-canvas dark:bg-canvas-dark">
 <div className="max-w-[1400px] mx-auto pb-16 md:pb-32">
 
 {isOffline && (
 <div className="mb-6 px-4 py-3 rounded-md bg-moss dark:bg-surface-dark text-ink dark:text-ink-dark text-sm font-medium flex items-center gap-3">
 <span className="w-2 h-2 rounded-full bg-ink-muted"></span>
 {language === 'hebrew' ? 'אתה לא מחובר לאינטרנט. טיוטה נשמרת במכשיר זה.' : 'You\'re offline. Draft is saved on this device.'}
 </div>
 )}

 {saveError && (
 <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 font-medium flex justify-between items-center">
 <span>{saveError}</span>
 <button onClick={() => setSaveError(null)} className="p-1 hover:bg-red-500/20 rounded-lg">
 <X size={16} />
 </button>
 </div>
 )}
 
 {availableDraft && (
 <div className="mb-6 px-4 py-3 rounded-md bg-progress-faint border border-progress/20 text-ink dark:text-ink-dark text-sm font-medium flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
 <span>{language === 'hebrew' ? 'קיימת טיוטה שמורה במכשיר זה.' : 'An unsaved draft exists on this device.'}</span>
 <div className="flex items-center gap-2">
 <button onClick={handleDiscardDraft} className="px-3 min-h-[36px] rounded-md border border-line text-ink hover:bg-moss dark:border-line-dark dark:text-ink-dark dark:hover:bg-moss-dark transition-colors">
 {language === 'hebrew' ? 'מחק טיוטה' : 'Discard'}
 </button>
 <button onClick={handleRestoreDraft} className="px-3 min-h-[36px] rounded-md font-semibold bg-forest-ondark text-white hover:bg-forest-ondark/90 dark:bg-forest dark:text-canvas-dark dark:hover:bg-forest-hover transition-colors">
 {language === 'hebrew' ? 'שחזר טיוטה' : 'Restore'}
 </button>
 </div>
 </div>
 )}

 <PlanWeekGrid />
 </div>
 </main>

      <ConfirmDialog
        open={activeConfirmOpen}
        title={language === 'hebrew' ? 'סקירת תוכנית אימונים' : 'Review Training Plan'}
        body={
          <div className="space-y-4">
            <div className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-lg p-4 space-y-2 text-sm text-ink dark:text-ink-dark">
              <div className="flex justify-between">
                <span className="text-ink-muted">{language === 'hebrew' ? 'לקוח:' : 'Client:'}</span>
                <span className="font-semibold">{clientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">{language === 'hebrew' ? 'תאריך התחלה:' : 'Start Date:'}</span>
                <span className="font-semibold">{assignment.active_from}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">{language === 'hebrew' ? 'משך:' : 'Duration:'}</span>
                <span className="font-semibold">{meta.duration_weeks || weekOrder.length} {language === 'hebrew' ? 'שבועות' : 'Weeks'}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-line dark:border-line-dark mt-2">
                <span className="text-ink-muted">{language === 'hebrew' ? 'סטטוס:' : 'Status:'}</span>
                <span className={`font-semibold ${assignment.status === 'active' ? 'text-forest' : 'text-amber-600'}`}>
                  {assignment.status === 'active' ? (language === 'hebrew' ? 'פעיל' : 'Active') : (language === 'hebrew' ? 'טיוטה' : 'Draft')}
                </span>
              </div>
            </div>
            
            {workoutOrder.length === 0 && assignment.status === 'active' && (
              <div className="text-red-600 bg-red-50 p-3 rounded-lg text-sm border border-red-200">
                {language === 'hebrew' ? 'אזהרה: התוכנית לא מכילה אימונים.' : 'Warning: This plan has no workouts.'}
              </div>
            )}
            
            {assignment.status === 'active' && (
              <p className="text-sm">
                {language === 'hebrew' 
                  ? `האם תרצה להקצות תוכנית זו ל-${clientName} החל מ-${assignment.active_from}?` 
                  : `Assign this plan to ${clientName} starting ${assignment.active_from}?`}
              </p>
            )}
          </div>
        }
        confirmLabel={language === 'hebrew' ? 'שמור תוכנית' : 'Save Plan'}
        isDestructive={false}
        loading={isSaving}
        onConfirm={() => {
          setActiveConfirmOpen(false);
          handleSavePlan();
        }}
        onCancel={() => setActiveConfirmOpen(false)}
        language={language}
      />

      <ConfirmDialog
        open={leaveConfirmOpen}
        title={language === 'hebrew' ? 'לצאת מבניית התוכנית?' : 'Are you sure you want to leave?'}
        body={language === 'hebrew'
          ? 'פרטי התוכנית עדיין לא הושלמו והתוכנית לא נשמרה.'
          : "The plan info isn't complete yet, and this plan hasn't been saved."}
        confirmLabel={language === 'hebrew' ? 'צא' : 'Leave'}
        isDestructive
        onConfirm={() => {
          setLeaveConfirmOpen(false);
          navigate('/training-management');
        }}
        onCancel={() => setLeaveConfirmOpen(false)}
        language={language}
      />

 <WorkoutEditDrawer />
 <ExerciseSettingsDrawer />

 {/* Plan Settings Slide-out Drawer */}
 <AnimatePresence>
 {isSettingsDrawerOpen && (
 <>
 <motion.div 
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 transition={{ duration: 0.3 }}
 className="fixed inset-0 bg-ink/20 dark:bg-ink/40 z-40 pointer-events-auto" 
 onClick={() => { if (!settingsIncomplete) closeSettingsDrawer(); else setLeaveConfirmOpen(true); }} 
 />
 <motion.div 
 initial={{ x: language === 'hebrew' ? '-100%' : '100%' }}
 animate={{ x: 0 }}
 exit={{ x: language === 'hebrew' ? '-100%' : '100%' }}
 transition={{ type: "spring", damping: 25, stiffness: 200 }}
 className={`fixed inset-y-0 ${language === 'hebrew' ? 'left-0 border-r' : 'right-0 border-l'} w-full sm:w-[450px] bg-surface dark:bg-surface-dark text-ink dark:text-ink-dark z-50 flex flex-col border-line dark:border-line-dark`} 
 dir={language === 'hebrew' ? 'rtl' : 'ltr'}
 >
 <div className="px-4 py-3 sm:p-6 border-b border-line dark:border-line-dark bg-surface dark:bg-surface-dark flex justify-between items-center">
 <h2 className="text-lg sm:text-xl font-bold">{language === 'hebrew' ? 'הגדרות תוכנית' : 'Plan Settings'}</h2>
 <button type="button" aria-label="Close" onClick={() => { if (!settingsIncomplete) closeSettingsDrawer(); else setLeaveConfirmOpen(true); }} className="p-2 min-h-11 min-w-11 flex items-center justify-center hover:bg-surface dark:hover:bg-canvas-dark rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-forest">
 <X size={20} />
 </button>
 </div>
 <div className="p-4 sm:p-6 flex-1 overflow-y-auto space-y-6">
 
 {settingsAttempted && settingsIncomplete && (
 <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-800 dark:border-red-700 dark:bg-red-950/50 dark:text-red-200">
 {language === 'hebrew' ? 'יש למלא את השדות המסומנים כדי להמשיך.' : 'Fill in the highlighted fields to continue.'}
 </div>
 )}
 
 <div className="space-y-2">
 <label className="text-sm font-semibold">{language === 'hebrew' ? 'לקוח *' : 'Client *'}</label>
 {clientError ? (
 <div className="flex items-center justify-between p-3 rounded-md border border-red-200 bg-red-50 text-red-600 dark:border-red-900 dark:bg-red-900/20 dark:text-red-400 text-sm">
 <span>{language === 'hebrew' ? 'שגיאה בטעינת לקוחות.' : 'Couldn\'t load clients.'}</span>
 <button onClick={fetchClientsList} className="font-semibold underline hover:no-underline">{language === 'hebrew' ? 'נסה שוב' : 'Retry'}</button>
 </div>
 ) : (
 <>
 {clients.length > 4 && (
 <input 
 type="text" 
 placeholder={language === 'hebrew' ? 'חפש לקוח...' : 'Search client...'}
 value={clientSearch}
 onChange={(e) => setClientSearch(e.target.value)}
 className="w-full p-2 mb-2 rounded-lg border border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark text-sm outline-none focus:border-forest"
 />
 )}
 <select 
 value={assignment.user_code} 
 onChange={(e) => updateAssignment({ user_code: e.target.value })} 
 className={`w-full p-3 rounded-md border bg-canvas dark:bg-surface-dark outline-none focus:ring-2 ${settingsAttempted && missingClient ? 'border-red-500 focus:ring-red-500/20' : 'border-line dark:border-line-dark focus:border-forest focus:ring-forest'}`}
 disabled={isLoadingClients}
 >
 <option value="" disabled>{isLoadingClients ? (language === 'hebrew' ? 'טוען...' : 'Loading...') : (language === 'hebrew' ? 'בחר לקוח...' : 'Select Client...')}</option>
 {clients.filter(c => (c.full_name || c.name || '').toLowerCase().includes(clientSearch.toLowerCase())).map(c => <option key={c.user_code} value={c.user_code}>{c.full_name || c.name} ({c.user_code})</option>)}
 </select>
 </>
 )}
 </div>

 <div className="space-y-2">
 <label className="text-sm font-semibold">{language === 'hebrew' ? 'שם התוכנית (כותרת) *' : 'Plan Name (Title) *'}</label>
 <input
 type="text" value={localName} onChange={(e) => setLocalName(e.target.value)} onBlur={() => handleBlur('name', localName)}
 className={`w-full p-3 rounded-md border bg-canvas dark:bg-surface-dark outline-none focus:ring-2 ${settingsAttempted && missingName ? 'border-red-500 focus:ring-red-500/20' : 'border-line dark:border-line-dark focus:border-forest focus:ring-forest'}`}
 />
 </div>

 {/* Plan Meta / Settings moved from Canvas to Drawer */}
 <div className="pt-4 border-t border-line dark:border-line-dark">
 <PlanMetaBar />
 </div>

 <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-4">
 <div className="space-y-2">
 <label className="text-sm font-semibold">{language === 'hebrew' ? 'תאריך התחלה *' : 'Start Date *'}</label>
 <input
 type="date" value={assignment.active_from} onChange={(e) => updateAssignment({ active_from: e.target.value })}
 className={`w-full p-3 rounded-md border bg-canvas dark:bg-surface-dark outline-none focus:ring-2 ${settingsAttempted && missingStart ? 'border-red-500 focus:ring-red-500/20' : 'border-line dark:border-line-dark focus:border-forest focus:ring-forest'}`}
 />
 </div>
 <div className="space-y-2">
 <label className="text-sm font-semibold">{language === 'hebrew' ? 'תאריך סיום (אופציונלי)' : 'End Date (Optional)'}</label>
 <input
 type="date" value={assignment.active_until || ''} onChange={(e) => updateAssignment({ active_until: e.target.value })}
 className="w-full p-3 rounded-md border border-line dark:border-line-dark bg-canvas dark:bg-surface-dark outline-none focus:ring-2 focus:border-forest focus:ring-forest"
 />
 </div>
 </div>

 <div className="space-y-2">
 <label className="text-sm font-semibold">{language === 'hebrew' ? 'הערות למתאמן' : 'Notes for Client'}</label>
 <textarea 
 value={localNotes} onChange={(e) => setLocalNotes(e.target.value)} onBlur={() => handleBlur('notes', localNotes)}
 className="w-full p-3 rounded-md border border-line dark:border-line-dark bg-canvas dark:bg-surface-dark outline-none focus:ring-2 focus:border-forest focus:ring-forest resize-none h-24"
 />
 </div>
 
 <AdvancedPlanSettings />

 </div>
 <div className="p-4 sm:p-6 border-t border-line dark:border-line-dark bg-surface dark:bg-surface-dark ">
            <button 
              onClick={() => { if (!settingsIncomplete) closeSettingsDrawer(); else setSettingsAttempted(true); }}
              className={`w-full py-4 font-bold rounded-md transition-colors ${missingClient ? 'bg-line dark:bg-line-dark text-ink-muted cursor-not-allowed opacity-70' : 'bg-forest hover:bg-forest-hover text-white'}`}
              disabled={missingClient}
            >
 {language === 'hebrew' ? 'המשך לתוכנית' : 'Continue to Plan'}
 </button>
 </div>
 </motion.div>
 </>
 )}
 </AnimatePresence>
 </div>
 );
}
